import {createContext, useCallback, useContext, useMemo, useState} from 'react';
import {assert} from 'assert-ts';
import {validate} from 'schemaDefinition/functions/validators';
import type {Data} from 'schemaDefinition/types/values';
import {getActualDataChangesWithSchema} from 'services/metadata';
import {useSnacks} from 'components/snacks';
import {useSchemaCodelists} from 'schema';
import {useSchemaThesauruses} from 'schema/hooks/useSchemaThesauruses';
import type {CompareEntitiesContextProps, CompareEntitiesType} from '../types';

export const CompareEntitiesContext = createContext<CompareEntitiesType>({
  target: {
    data: {},
    schema: {
      name: 'target',
      parts: [],
    },
  },
  source: undefined,
  onChange: () => undefined,
  onSave: () => Promise.resolve(),
  showFullSource: false,
  toggleShowFullSource: () => undefined,
});

export const useCompareEntitiesContextProviderValue = ({
  targetData,
  sourceData,
  schema,
  onSave,
}: CompareEntitiesContextProps): CompareEntitiesType => {
  const codelistMap = useSchemaCodelists(schema);
  const thesaurusMap = useSchemaThesauruses(schema);
  const {errorSnack, warningSnack} = useSnacks();
  const [showFullSource, setShowFullSource] = useState(false);

  const toggleShowFullSource = useCallback(
    () => setShowFullSource(!showFullSource),
    [showFullSource],
  );

  const {changesData, changesSchema} = useMemo(() => {
    const changes =
      targetData && sourceData
        ? getActualDataChangesWithSchema(
            sourceData,
            targetData,
            schema,
            thesaurusMap,
            {
              excludeEmpty: !showFullSource,
              excludeEqual: !showFullSource,
              excludeReadOnly: false,
            },
          )
        : undefined;

    return {
      changesData: changes?.actualChanges,
      changesSchema: changes?.trimmedSchema,
    };
  }, [targetData, sourceData, schema, thesaurusMap, showFullSource]);

  const [editData, setEditData] = useState<Data>(targetData);

  const onChange = useCallback((data: Data) => {
    if (data) {
      setEditData(data);
    } else {
      setEditData({});
    }
  }, []);

  const handleSave = useCallback(() => {
    if (editData) {
      const validation = validate(
        editData,
        undefined,
        schema,
        assert(codelistMap, 'CompareEntitiesContext: codelistMap is required'),
      );

      if (validation.valid !== 'error') {
        if (validation.valid === 'warning') {
          warningSnack(validation.errors.map(e => e.value).join(', '));
        }

        return onSave(editData, sourceData);
      }

      if (validation.errors.filter(e => e.value).length > 0) {
        errorSnack(validation.errors.map(e => e.value).join(', '));
      }
    }

    return Promise.reject();
  }, [
    codelistMap,
    editData,
    errorSnack,
    onSave,
    sourceData,
    schema,
    warningSnack,
  ]);

  return useMemo(() => {
    return {
      onChange,
      onSave: handleSave,
      target: {
        data: editData,
        schema: schema,
      },
      source:
        changesData && changesSchema
          ? {
              data: changesData,
              schema: changesSchema,
            }
          : undefined,
      showFullSource,
      toggleShowFullSource,
    };
  }, [
    onChange,
    handleSave,
    editData,
    schema,
    changesData,
    changesSchema,
    showFullSource,
    toggleShowFullSource,
  ]);
};

export const useCompareEntitiesContext = () => {
  return assert(
    useContext(CompareEntitiesContext),
    'CompareEntitiesContext: context expected',
  );
};
